import { AfterViewInit, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Radio } from '@shared/controls/radio/radio';
import { Select } from '@shared/controls/select/select';
import type { SelectItem } from '@appTypes';
import { MultiagendaDay } from 'src/app/modules/multiagenda/multiagenda-day/multiagenda-day';
import { MultiagendaService, SeguimientoReprogramar, FechaHora, PostReprogramacionRequest } from 'src/app/modules/multiagenda/multiagenda.service';
import { Shared } from '@shared/exports';
import { Alerts } from '@services/alerts/alerts';

export interface ReprogramarDialogData {
  idDistribuidor: string;
  idPropietario: string;
  idSeguimiento: string;
}

export type TipoReprogramacion = 'no-contactar' | 'otro-momento';
export type OpcionReprogramar = '1-hora' | '2-horas' | '3-horas' | '1-dia' | '2-dias' | 'otro';

export interface ReprogramarSeguimientoData {
  tipo: TipoReprogramacion;
  reprogramar: OpcionReprogramar;
  observaciones: string;
  fechaHorario: Date;
}

@Component({
  selector: 'app-home-reprogramar-seguimientos',
  standalone: true,
  imports: [FormsModule, Radio, Select, MultiagendaDay, Shared],
  templateUrl: './home-reprogramar-seguimientos.html',
  styleUrl: './home-reprogramar-seguimientos.scss',
})
export class HomeReprogramarSeguimientos implements AfterViewInit {
  private readonly multiagendaService = inject(MultiagendaService);
  private readonly dialogRef = inject(DialogRef, { optional: true });
  private readonly dialogData = inject<ReprogramarDialogData>(DIALOG_DATA, { optional: true });
  private readonly alerts = inject(Alerts);
  // Inputs (usados cuando no viene de diálogo)
  $useSelectMode = input<boolean>(true, { alias: 'useSelectMode' });
  $idDistribuidorInput = input<string>('', { alias: 'idDistribuidor' });
  $idPropietarioInput = input<string>('', { alias: 'idPropietario' });
  $idSeguimientoInput = input<string>('', { alias: 'idSeguimiento' });

  // Computed que usa datos del diálogo o inputs
  $idDistribuidor = computed(() => this.dialogData?.idDistribuidor || this.$idDistribuidorInput() || '153982');
  $idPropietario = computed(() => this.dialogData?.idPropietario || this.$idPropietarioInput() || '1539820001');
  $idSeguimiento = computed(() => this.dialogData?.idSeguimiento || this.$idSeguimientoInput() || '153982000127255A0003');

  // Outputs
  confirmar = output<ReprogramarSeguimientoData>();

  // Signals
  $tipoSeleccionado = signal<TipoReprogramacion>('no-contactar');
  $reprogramarSeleccionado = signal<OpcionReprogramar>('1-hora');
  $observaciones = signal<string>('');
  $fechaHorario = signal<Date>(this.calcularFechaHorario('1-hora'));

  // Signals para fecha/hora/minutos personalizados (cuando se selecciona "Otro")
  $fechaPersonalizada = signal<string>(this.getMinDate());
  $horaPersonalizada = signal<string>('');
  $minutosPersonalizados = signal<string>('');

  // Estado de disponibilidad
  $horariosOcupados = signal<SeguimientoReprogramar[]>([]);
  $verificandoDisponibilidad = signal<boolean>(false);
  $horarioDisponible = signal<boolean>(true);

  // Fecha para sincronizar con multiagenda-day
  $fechaMultiagenda = signal<Date>(new Date());

  // Opciones de hora (00-23)
  readonly horasOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  // Opciones de minutos (00, 05, 10, 15, ..., 55)
  readonly minutosOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  // Opciones de tipo (para radios)
  readonly tiposOptions: { value: TipoReprogramacion; label: string }[] = [
    { value: 'no-contactar', label: 'No puede contactar al prospecto' },
    { value: 'otro-momento', label: 'El prospecto solicita ser contactado en otro momento' },
  ];

  // Opciones de tipo (para select)
  readonly tiposSelectItems: SelectItem[] = [
    { key: '1', value: 'No puede contactar al prospecto' },
    { key: '2', value: 'El prospecto solicita ser contactado en otro momento' },
  ];

  // Opciones de reprogramar (para radios)
  readonly reprogramarOptions: { value: OpcionReprogramar; label: string }[] = [
    { value: '1-hora', label: '1 Hora' },
    { value: '2-horas', label: '2 Horas' },
    { value: '3-horas', label: '3 Horas' },
    { value: '1-dia', label: '1 Día' },
    { value: '2-dias', label: '2 Días' },
    { value: 'otro', label: 'Otro' },
  ];

  // Opciones de reprogramar (para select)
  readonly reprogramarSelectItems: SelectItem[] = [
    { key: '1-hora', value: '1 Hora' },
    { key: '2-horas', value: '2 Horas' },
    { key: '3-horas', value: '3 Horas' },
    { key: '1-dia', value: '1 Día' },
    { key: '2-dias', value: '2 Días' },
    { key: 'otro', value: 'Otro' },
  ];

  // Opciones de hora (para select)
  readonly horasSelectItems: SelectItem[] = Array.from({ length: 24 }, (_, i) => ({
    key: i.toString().padStart(2, '0'),
    value: i.toString().padStart(2, '0'),
  }));

  // Opciones de minutos (para select)
  readonly minutosSelectItems: SelectItem[] = Array.from({ length: 12 }, (_, i) => ({
    key: (i * 5).toString().padStart(2, '0'),
    value: (i * 5).toString().padStart(2, '0'),
  }));

  ngAfterViewInit(): void {
    // Delay para asegurar que los inputs estén disponibles
    setTimeout(() => this.verificarDisponibilidad(), 0);
  }

  onTipoChange(value: string): void {
    this.$tipoSeleccionado.set(value as TipoReprogramacion);
  }

  onReprogramarChange(value: string): void {
    const opcion = value as OpcionReprogramar;
    this.$reprogramarSeleccionado.set(opcion);
    if (opcion !== 'otro') {
      this.$fechaHorario.set(this.calcularFechaHorario(opcion));
      this.verificarDisponibilidad();
    } else {
      // Para "otro", inicializar con hora actual
      const ahora = new Date();
      this.$fechaPersonalizada.set(this.formatDateForInput(ahora));
      this.$horaPersonalizada.set(ahora.getHours().toString().padStart(2, '0'));
      // Redondear minutos al múltiplo de 5 más cercano (máximo 55)
      const minutosRedondeados = Math.min(Math.ceil(ahora.getMinutes() / 5) * 5, 55);
      this.$minutosPersonalizados.set(minutosRedondeados.toString().padStart(2, '0'));
      // Actualizar fechaHorario directamente con la fecha actual
      this.$fechaHorario.set(ahora);
      this.verificarDisponibilidad();
    }
  }

  onFechaChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.$fechaPersonalizada.set(target.value);
    this.actualizarFechaHorarioPersonalizado();
  }

  onHoraChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.$horaPersonalizada.set(target.value);
    this.actualizarFechaHorarioPersonalizado();
  }

  onMinutosChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.$minutosPersonalizados.set(target.value);
    this.actualizarFechaHorarioPersonalizado();
  }

  getMinDate(): string {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
  }

  actualizarFechaHorarioPersonalizado(): void {
    const fecha = this.$fechaPersonalizada();
    const hora = this.$horaPersonalizada();
    const minutos = this.$minutosPersonalizados();

    // Solo procesar si todos los valores están presentes
    if (fecha && hora !== '' && minutos !== '') {
      const nuevaFecha = new Date(`${fecha}T${hora}:${minutos}:00`);
      // Validar que la fecha sea válida antes de actualizar
      if (!isNaN(nuevaFecha.getTime())) {
        this.$fechaHorario.set(nuevaFecha);
        this.verificarDisponibilidad();
      }
    }
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateForApi(date: Date): string {
    return this.formatDateForInput(date);
  }

  private verificarDisponibilidad(): void {
    const fechaHorario = this.$fechaHorario();
    this.cargarSeguimientosParaFecha(fechaHorario);
  }

  // Método para cargar seguimientos de una fecha específica
  private cargarSeguimientosParaFecha(fecha: Date): void {
    // Validar que la fecha sea válida
    if (!fecha || isNaN(fecha.getTime())) {
      console.warn('Fecha inválida, no se cargan seguimientos');
      return;
    }

    // Usar valores por defecto si los inputs están vacíos
    const idDist = this.$idDistribuidor() || '153982';
    const idProp = this.$idPropietario() || '1539820001';
    const idSeg = this.$idSeguimiento() || '153982000127255A0003';

    console.log('PARAMETROS RECIBIDOS');
    console.log('idDistribuidor', idDist);
    console.log('idPropietario', idProp);
    console.log('idSeguimiento', idSeg);

    const fechaStr = this.formatDateForApi(fecha);
    this.$verificandoDisponibilidad.set(true);
    this.$fechaMultiagenda.set(fecha);

    console.log('Padre - Cargando seguimientos para fecha:', fechaStr);
    this.multiagendaService.getReprogramarSeguimiento({
      idDistribuidor: idDist,
      idPropietario: idProp,
      idSeguimiento: idSeg,
      fechaInicio: fechaStr,
      fechaFin: fechaStr
    }).subscribe({
      next: (resp) => {
        console.log('Padre - Respuesta API:', resp);
        if (resp?.success && Array.isArray(resp?.seguimientos)) {
          console.log('Padre - Seguimientos encontrados:', resp.seguimientos.length);
          this.$horariosOcupados.set(resp.seguimientos);
          console.log('Padre - Signal $horariosOcupados actualizado:', this.$horariosOcupados().length);
          this.$horarioDisponible.set(!this.estaHorarioOcupado(this.$fechaHorario(), resp.seguimientos));
        } else {
          this.$horariosOcupados.set([]);
          this.$horarioDisponible.set(true);
        }
        this.$verificandoDisponibilidad.set(false);
      },
      error: (err) => {
        console.error('Error verificando disponibilidad:', err);
        this.$horariosOcupados.set([]);
        this.$horarioDisponible.set(true);
        this.$verificandoDisponibilidad.set(false);
      }
    });
  }

  // Manejar cambio de fecha desde multiagenda-day
  onMultiagendaFechaChange(fecha: Date): void {
    this.cargarSeguimientosParaFecha(fecha);
  }

  private estaHorarioOcupado(fecha: Date, seguimientos: SeguimientoReprogramar[]): boolean {
    const horaSeleccionada = fecha.getHours();
    const minutosSeleccionados = fecha.getMinutes();

    return seguimientos.some(seg => {
      const segFecha = this.fechaHoraToDate(seg.Programada);
      // Verificar si es el mismo día
      if (segFecha.getDate() !== fecha.getDate() ||
        segFecha.getMonth() !== fecha.getMonth() ||
        segFecha.getFullYear() !== fecha.getFullYear()) {
        return false;
      }
      // Verificar si la hora está dentro del rango del seguimiento (±30 minutos)
      const segHora = segFecha.getHours();
      const segMinutos = segFecha.getMinutes();
      const segTotalMinutos = segHora * 60 + segMinutos;
      const selTotalMinutos = horaSeleccionada * 60 + minutosSeleccionados;
      // Consideramos ocupado si está dentro de ±30 minutos
      return Math.abs(segTotalMinutos - selTotalMinutos) < 30;
    });
  }

  private fechaHoraToDate(fh: FechaHora): Date {
    return new Date(
      fh.date.year,
      fh.date.month - 1,
      fh.date.day,
      fh.time.hour,
      fh.time.minute,
      fh.time.second
    );
  }

  onObservacionesChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.$observaciones.set(target.value);
  }

  // Validar si se puede reprogramar (horario disponible y observaciones no vacías)
  puedeReprogramar(): boolean {
    const horarioDisponible = this.$horarioDisponible();
    const observaciones = this.$observaciones().trim();
    const noVerificando = !this.$verificandoDisponibilidad();
    return horarioDisponible && observaciones.length > 0 && noVerificando;
  }

  // Llamar al API de reprogramar
  onReprogramar(): void {
    if (!this.puedeReprogramar()) {
      return;
    }

    // Formatear fecha como "YYYY-MM-DD HH:mm:ss"
    const fecha = this.$fechaHorario();
    const year = fecha.getFullYear();
    const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const day = fecha.getDate().toString().padStart(2, '0');
    const hours = fecha.getHours().toString().padStart(2, '0');
    const minutes = fecha.getMinutes().toString().padStart(2, '0');
    const programada = `${year}-${month}-${day} ${hours}:${minutes}:00`;

    // Obtener tipoEvaluacion basado en el tipo seleccionado
    const tipoEvaluacion = this.$tipoSeleccionado() === 'no-contactar' ? '1' : '2';

    const request: PostReprogramacionRequest = {
      idDistribuidor: this.$idDistribuidor(),
      idSeguimiento: this.$idSeguimiento(),
      programada,
      observaciones: this.$observaciones(),
      tipoEvaluacion,
    };

    console.log('📤 Enviando reprogramación:', request);

    this.multiagendaService.postReprogramacion(request).subscribe({
      next: (response) => {
        console.log('✅ Respuesta reprogramación:', response);
        this.alerts.show('success', { title: 'Generado Correctamente', msg: response.message }).then(() => {
          if (response.success) {
            this.confirmar.emit({
              tipo: this.$tipoSeleccionado(),
              reprogramar: this.$reprogramarSeleccionado(),
              observaciones: this.$observaciones(),
              fechaHorario: this.$fechaHorario(),
            });
          }
          this.dialogRef?.close();
        });
      },
      error: (error) => {
        console.error('❌ Error en reprogramación:', error);
        this.alerts.show('error', { title: 'Error al reprogramar', msg: (error.message || 'Error desconocido') });
      }
    });
  }

  onConfirmar(): void {
    this.confirmar.emit({
      tipo: this.$tipoSeleccionado(),
      reprogramar: this.$reprogramarSeleccionado(),
      observaciones: this.$observaciones(),
      fechaHorario: this.$fechaHorario(),
    });
  }

  get horarioFormateado(): string {
    const fecha = this.$fechaHorario();
    const year = fecha.getFullYear().toString().slice(-2);
    const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const day = fecha.getDate().toString().padStart(2, '0');
    const hours = fecha.getHours().toString().padStart(2, '0');
    const minutes = fecha.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  private calcularFechaHorario(opcion: OpcionReprogramar): Date {
    const ahora = new Date();
    switch (opcion) {
      case '1-hora':
        return new Date(ahora.getTime() + 1 * 60 * 60 * 1000);
      case '2-horas':
        return new Date(ahora.getTime() + 2 * 60 * 60 * 1000);
      case '3-horas':
        return new Date(ahora.getTime() + 3 * 60 * 60 * 1000);
      case '1-dia':
        return new Date(ahora.getTime() + 24 * 60 * 60 * 1000);
      case '2-dias':
        return new Date(ahora.getTime() + 48 * 60 * 60 * 1000);
      case 'otro':
        return ahora;
    }
  }
}
